'use client'

import { useMemo } from 'react'
import { useChatBrandId } from '@/components/chat/chat-brand-context'
import { useOptionalBrandContext } from '@/contexts/brand-context'
import { useOptionalOrgContext } from '@/contexts/org-context'
import type { ChatFileScope } from '@/lib/shared/blob/chat-file-paths'

/**
 * Where this surface's document attachments upload in the private Blob store.
 * The brand should be the one the chat request names, so the server's read
 * check passes without a second lookup: inside a thread that is the thread's
 * brand (`ChatBrandProvider`, the `customBrandId` the runtime sends — a
 * `/chat/<id>` extraction chat is bound to a brand that is not the active
 * one); elsewhere (the launch composer) it is the active brand. A file
 * uploaded under another brand the requester can reach is still read (the
 * server asks `canReadBrand`). Undefined outside an org, which makes a
 * document upload fail with a reload hint instead of landing anywhere public.
 */
export function useChatFileScope(): ChatFileScope | undefined {
  const orgId = useOptionalOrgContext()?.orgId
  const threadBrandId = useChatBrandId()
  const activeBrandId = useOptionalBrandContext()?.brandId
  const brandId = threadBrandId || activeBrandId
  return useMemo(
    () => (orgId ? { orgId, ...(brandId ? { brandId } : {}) } : undefined),
    [orgId, brandId]
  )
}
